import React from "react";
import styled from "styled-components";
import { colors } from "../../../Shared/colors";
import CloseIcon from "@mui/icons-material/Close";
import PhoneIcon from "@mui/icons-material/Phone";
import ScheduleIcon from "@mui/icons-material/Schedule";
import WarningIcon from "@mui/icons-material/Warning";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import InfoIcon from "@mui/icons-material/Info";

export const TelesalesNotifications = ({ notifications, onClose }) => {
  const getNotificationIcon = (type) => {
    switch (type) {
      case 'call':
        return PhoneIcon;
      case 'meeting':
        return ScheduleIcon;
      case 'warning':
        return WarningIcon;
      case 'success':
        return CheckCircleIcon;
      default:
        return InfoIcon;
    }
  };

  const getNotificationColor = (type) => {
    switch (type) {
      case 'call':
        return '#3b82f6';
      case 'meeting':
        return '#8b5cf6';
      case 'warning':
        return '#f59e0b';
      case 'success':
        return '#10b981';
      default:
        return '#6b7280';
    }
  };

  const getNotificationBgColor = (type) => {
    switch (type) {
      case 'call':
        return '#dbeafe';
      case 'meeting':
        return '#ede9fe';
      case 'warning':
        return '#fef3c7';
      case 'success':
        return '#d1fae5';
      default:
        return '#f1f5f9';
    }
  };

  const displayNotifications = notifications || [];

  return (
    <NotificationOverlay>
      <NotificationModal>
        <ModalHeader>
          <HeaderTitle>
            <NotificationIcon>🔔</NotificationIcon>
            <span>Notifications</span>
          </HeaderTitle>
          <CloseButton onClick={onClose}>
            <CloseIcon />
          </CloseButton>
        </ModalHeader>

        <NotificationList>
          {displayNotifications.length === 0 && (
            <div style={{ padding: '2rem', textAlign: 'center', color: '#666', fontSize: '14px' }}>
              No notifications
            </div>
          )}
          {displayNotifications.map((notification) => {
            const IconComponent = getNotificationIcon(notification.type);
            const iconColor = getNotificationColor(notification.type);
            const bgColor = getNotificationBgColor(notification.type);

            return (
              <NotificationItem key={notification.id} priority={notification.priority}>
                <NotificationIconContainer bgColor={bgColor}>
                  <IconComponent style={{ color: iconColor, fontSize: '1.25rem' }} />
                </NotificationIconContainer>
                
                <NotificationContent>
                  <NotificationHeader>
                    <NotificationTitle>{notification.title}</NotificationTitle>
                    <NotificationTime>{notification.time}</NotificationTime>
                  </NotificationHeader>
                  
                  <NotificationMessage>{notification.message}</NotificationMessage>
                  
                  <NotificationActions>
                    <ActionButton primary>
                      View Details
                    </ActionButton>
                    <ActionButton>
                      Mark as Read
                    </ActionButton>
                  </NotificationActions>
                </NotificationContent>
              </NotificationItem>
            );
          })}
        </NotificationList>

        <ModalFooter>
          <FooterActions>
            <ActionButton secondary>
              Mark All as Read
            </ActionButton>
            <ActionButton>
              View All Notifications
            </ActionButton>
          </FooterActions>
        </ModalFooter>
      </NotificationModal>
    </NotificationOverlay>
  );
};

// Styled Components
const NotificationOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
`;

const NotificationModal = styled.div`
  background: ${colors.white};
  border-radius: 16px;
  width: 100%;
  max-width: 600px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid #e2e8f0;
`;

const HeaderTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 1.25rem;
  font-weight: 600;
  color: ${colors.text};
`;

const NotificationIcon = styled.span`
  font-size: 1.5rem;
`;

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border: none;
  background: transparent;
  color: #64748b;
  cursor: pointer;
  border-radius: 6px;
  transition: all 0.3s ease;
  
  &:hover {
    background: #f1f5f9;
    color: ${colors.text};
  }
`;

const NotificationList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const NotificationItem = styled.div`
  display: flex;
  gap: 1rem;
  padding: 1rem;
  background: #f8fafc;
  border-radius: 12px;
  border-left: 4px solid ${props => {
    switch (props.priority) {
      case 'high': return '#ef4444';
      case 'medium': return '#f59e0b';
      case 'low': return '#10b981';
      default: return '#6b7280';
    }
  }};
  transition: all 0.3s ease;
  
  &:hover {
    background: #f1f5f9;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
`;

const NotificationIconContainer = styled.div`
  width: 3rem;
  height: 3rem;
  border-radius: 8px;
  background: ${props => props.bgColor};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const NotificationContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const NotificationHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
`;

const NotificationTitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  color: ${colors.text};
  margin: 0;
  flex: 1;
`;

const NotificationTime = styled.span`
  font-size: 0.75rem;
  color: #64748b;
  white-space: nowrap;
`;

const NotificationMessage = styled.p`
  color: #64748b;
  margin: 0;
  font-size: 0.875rem;
  line-height: 1.4;
`;

const NotificationActions = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 0.5rem;
`;

const ActionButton = styled.button`
  padding: 0.5rem 1rem;
  border: 1px solid ${props => props.primary ? '#667eea' : '#e2e8f0'};
  border-radius: 6px;
  background: ${props => props.primary ? '#667eea' : 'transparent'};
  color: ${props => props.primary ? 'white' : colors.text};
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: ${props => props.primary ? '#5a67d8' : '#f8fafc'};
    transform: translateY(-1px);
  }
`;

const ModalFooter = styled.div`
  padding: 1.5rem;
  border-top: 1px solid #e2e8f0;
  background: #f8fafc;
  border-radius: 0 0 16px 16px;
`;

const FooterActions = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 1rem;
`;

